import React, { useState, useEffect } from 'react';
import AuthService from '../../services/authService';
import { apiErrToMessage } from '../../utils/apiError';
import { dispatchQuotaUpdated } from '../../utils/quotaEvent';
import ProtectedMenuPage from '../../components/ProtectedMenuPage/ProtectedMenuPage';
import KakaoFloatButton from '../../components/KakaoFloatButton/KakaoFloatButton';
import './CountText.css';
import {
    Card,
    Row,
    Col,
    Button,
    Typography,
    Space,
    Input,
    Tag,
    Table,
    message,
    Divider,
} from 'antd';
import { SearchOutlined, ReloadOutlined, BarChartOutlined } from '@ant-design/icons';

const { Text } = Typography;
const { CheckableTag } = Tag;
const { TextArea } = Input;

const CountText = () => {
    const [productText, setProductText] = useState('');
    const [baseKeyword, setBaseKeyword] = useState('');
    const [keywords, setKeywords] = useState([]);
    const [selectedKeywords, setSelectedKeywords] = useState([]);
    const [searchResult, setSearchResult] = useState(null);

    const [_sortKey, setSortKey] = useState('');
    const [_sortOrder, setSortOrder] = useState(null);
    const calcTableScrollY = () => {
        const header = 72;
        const pagePad = 48;
        const cardHead = 48;
        const cardBodyPad = 48;
        const buffer = 40;
        return Math.max(
            300,
            window.innerHeight - header - pagePad - cardHead - cardBodyPad - buffer
        );
    };
    const [tableScrollY, setTableScrollY] = useState(() => calcTableScrollY());

    useEffect(() => {
        const update = () => setTableScrollY(calcTableScrollY());
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, []);

    useEffect(() => {
        if (!productText.trim()) {
            setKeywords([]);
            return;
        }
        const parts = productText.split(/\s+/).filter(Boolean);
        setKeywords(parts);
    }, [productText]);

    const handleKeywordClick = word => {
        if (selectedKeywords.includes(word)) {
            const updated = selectedKeywords.filter(w => w !== word);
            setSelectedKeywords(updated);
            setBaseKeyword(updated.join(' '));
            message.info(`"${word}" 제거됨`);
            return;
        }

        const updated = [...selectedKeywords, word].slice(-5);
        setSelectedKeywords(updated);
        setBaseKeyword(updated.join(' '));
        message.info(`"${word}" 추가됨 (${updated.length}/5)`);
    };

    const totalChars = productText.length;
    const noSpaces = productText.replace(/\s/g, '').length;
    const wordCount = productText.trim() ? productText.trim().split(/\s+/).length : 0;

    const handleSearch = async () => {
        if (!baseKeyword.trim()) {
            message.warning('키워드를 선택하거나 입력해주세요.');
            return;
        }

        const cleanedKeyword = baseKeyword.replace(/\s+/g, '').trim();

        try {
            const res = await AuthService.authAxios.post('/tools/search/value', {
                keyword: cleanedKeyword,
                detail: 1,
            });

            if (res.status === 200) {
                const result = res.data;
                const item = result?.data?.[0];

                if (item?.success && Array.isArray(item?.data) && item.data.length > 0) {
                    setSearchResult(result);
                    setSortKey('');
                    setSortOrder(null);
                    dispatchQuotaUpdated('countText');
                    message.success('검색량 조회 완료');
                    return;
                }

                if (item?.success && (!item.data || item.data.length === 0)) {
                    setSearchResult(null);
                    message.warning('검색 결과가 없습니다.');
                    return;
                }

                if (item?.success === false) {
                    const errMsg = item?.error || '❌ 키워드 API 요청 실패';
                    message.error(errMsg);
                    return;
                }

                message.warning('예기치 못한 응답 형식입니다.');
                return;
            }

            message.error(`서버 응답 오류 (${res.status})`);
        } catch (error) {
            const { data, status } = error.response || {};
            const msg = apiErrToMessage(data, status);
            message.error(msg);
            console.error('검색 요청 실패:', error);
        }
    };

    const columns = [
        {
            title: '키워드',
            dataIndex: 'relKeyword',
            key: 'relKeyword',
            ellipsis: true,
            render: t => <span className="count-text-kw-cell">{t}</span>,
        },
        {
            title: '월간 검색량',
            key: 'totalSearch',
            align: 'right',
            width: 120,
            sorter: (a, b) => {
                const totalA = toNumber(a.monthlyPcQcCnt) + toNumber(a.monthlyMobileQcCnt);
                const totalB = toNumber(b.monthlyPcQcCnt) + toNumber(b.monthlyMobileQcCnt);
                return totalA - totalB;
            },
            defaultSortOrder: 'descend',
            render: (_, record) => {
                const pc = toNumber(record.monthlyPcQcCnt);
                const mo = toNumber(record.monthlyMobileQcCnt);
                const total = pc + mo;
                return <span className="count-text-vol-cell">{total.toLocaleString()}</span>;
            },
        },
        {
            title: '경쟁도',
            dataIndex: 'compIdx',
            key: 'compIdx',
            align: 'center',
            width: 88,
            render: text => <span className="count-text-comp-cell">{text || '-'}</span>,
        },
        {
            title: 'PC / 모바일 비율',
            key: 'ratio',
            width: 220,
            render: (_, record) => {
                const pc = toNumber(record.monthlyPcQcCnt);
                const mo = toNumber(record.monthlyMobileQcCnt);
                const total = pc + mo;
                const pcRatio = total ? Math.round((pc / total) * 100) : 0;
                const moRatio = total ? Math.round((mo / total) * 100) : 0;
                return (
                    <div className="count-text-ratio-bar">
                        <div className="count-text-ratio-track">
                            <div className="count-text-ratio-pc" style={{ width: `${pcRatio}%` }} />
                            <div className="count-text-ratio-mo" style={{ width: `${moRatio}%` }} />
                        </div>
                        <div className="count-text-ratio-labels">
                            <span className="count-text-ratio-pct">PC {pcRatio}%</span>
                            <span className="count-text-ratio-pct">MO {moRatio}%</span>
                        </div>
                    </div>
                );
            },
        },
    ];

    const toNumber = val => {
        if (typeof val === 'number') return val;
        if (!val) return 0;
        const str = String(val).trim();
        if (str.includes('<')) return 0;
        const cleaned = str.replace(/[^\d]/g, '');
        return cleaned ? Number(cleaned) : 0;
    };

    return (
        <ProtectedMenuPage menuKey="countText">
            <div className="count-text-page">
                <div className="page-content-inner">
                    <Row gutter={24} align="stretch">
                        <Col xl={10} lg={12} md={24} className="count-text-col-left">
                            <Card
                                title="텍스트 입력"
                                bordered={false}
                                className="control-card count-text-input-card"
                            >
                                <div className="count-text-textarea-wrapper">
                                    <TextArea
                                        placeholder="글자 수를 셀 텍스트나 상품명을 입력하세요."
                                        value={productText}
                                        onChange={e => setProductText(e.target.value)}
                                        className="count-text-textarea"
                                        style={{ resize: 'none' }}
                                    />
                                </div>
                                <div className="count-text-stats-inline">
                                    <span>
                                        전체 글자 수 <strong>{totalChars}</strong>자
                                    </span>
                                    <span>
                                        공백 제외 <strong>{noSpaces}</strong>
                                    </span>
                                    <span>
                                        단어 수 <strong>{wordCount}</strong>
                                    </span>
                                </div>

                                {keywords.length > 0 && (
                                    <div className="count-text-keyword-section">
                                        <Divider orientation="left" plain>
                                            키워드 선택 (최대 5개)
                                        </Divider>
                                        <Space wrap size="small">
                                            {keywords.map((word, i) => {
                                                const order = selectedKeywords.indexOf(word);
                                                const isActive = order >= 0;
                                                return (
                                                    <CheckableTag
                                                        key={i}
                                                        checked={isActive}
                                                        onChange={() => handleKeywordClick(word)}
                                                        className="count-text-keyword-tag"
                                                    >
                                                        {isActive && (
                                                            <Text
                                                                strong
                                                                style={{
                                                                    marginRight: '4px',
                                                                    color: 'inherit',
                                                                }}
                                                            >
                                                                {order + 1}.
                                                            </Text>
                                                        )}
                                                        {word}
                                                    </CheckableTag>
                                                );
                                            })}
                                            <Button
                                                size="small"
                                                className="page-action-btn"
                                                icon={<ReloadOutlined />}
                                                onClick={() => {
                                                    setBaseKeyword('');
                                                    setSelectedKeywords([]);
                                                }}
                                            >
                                                선택 초기화
                                            </Button>
                                        </Space>
                                    </div>
                                )}

                                <Divider plain />

                                <Row gutter={12} align="middle">
                                    <Col flex="auto">
                                        <Input
                                            placeholder="기본 키워드를 입력하거나 위에서 선택하세요."
                                            value={baseKeyword}
                                            onChange={e => setBaseKeyword(e.target.value)}
                                            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                                            className="count-text-search-input"
                                            style={{ fontSize: 12, height: 32 }}
                                        />
                                    </Col>
                                    <Col>
                                        <Button
                                            type="primary"
                                            icon={<BarChartOutlined />}
                                            onClick={handleSearch}
                                            className="search-btn-premium"
                                            style={{ height: 32, fontSize: 12 }}
                                        >
                                            검색량 조회
                                        </Button>
                                    </Col>
                                </Row>
                            </Card>
                        </Col>

                        <Col xl={14} lg={12} md={24} className="count-text-col-right">
                            <Card
                                title={
                                    searchResult ? (
                                        <>
                                            월간 검색량 결과{' '}
                                            <Tag color="blue" style={{ fontSize: '12px' }}>
                                                {searchResult.data?.[0]?.data?.length || 0}개 키워드
                                            </Tag>
                                        </>
                                    ) : (
                                        '월간 검색량 결과'
                                    )
                                }
                                bordered={false}
                                className="count-text-result-card"
                                bodyStyle={{
                                    padding: '12px 16px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flex: 1,
                                    minHeight: 0,
                                }}
                            >
                                {searchResult ? (
                                    <div className="count-text-table-section">
                                        <Table
                                            columns={columns}
                                            dataSource={searchResult.data?.[0]?.data}
                                            rowKey="relKeyword"
                                            pagination={false}
                                            size="small"
                                            tableLayout="fixed"
                                            scroll={{ y: tableScrollY }}
                                            className="count-text-result-table"
                                        />
                                    </div>
                                ) : (
                                    <div className="count-text-empty">
                                        <Text type="secondary">
                                            키워드를 선택하고 검색량 조회를 실행하세요.
                                        </Text>
                                    </div>
                                )}
                            </Card>
                        </Col>
                    </Row>
                </div>
            </div>
            <KakaoFloatButton />
        </ProtectedMenuPage>
    );
};

export { CountText };
